import { type ReactNode } from 'react';
import { User } from 'lucide-react';
import { ConnectionLine } from './connection-line';
import styles from './empty-state.module.css';

export interface EmptyStateStep {
  readonly title: string;
  readonly body?: string;
  /** Adım tek dokunuşla ulaşılabilir bir yere gider (README 4.7). */
  readonly href?: string;
}

export interface EmptyStateProps {
  readonly kicker?: string;
  readonly title: string;
  /**
   * Başlığın HANGİ öğeye çizileceği. Varsayılan `p`, çünkü boş durum çoğu
   * yerde SAYFA İÇİ bir bölümdür ve sayfanın kendi `h1`i zaten vardır; orada
   * başlık üretmek başlık sırasını bozar. Boş durumun KENDİSİ sayfanın ana
   * içeriği olduğunda (404 gibi) çağıran `h1` verir, yoksa sayfa hiç başlık
   * taşımaz (WCAG 2.4.6 · bulundu: 404 sayfasında tek bir başlık yoktu).
   */
  readonly titleAs?: 'p' | 'h1' | 'h2';
  readonly body?: ReactNode;
  /** En fazla üç numaralı ilk adım. */
  readonly steps?: readonly EmptyStateStep[];
  /** Tek dolu eylem + (varsa) çerçeveli ikinci seçenek. */
  readonly action?: ReactNode;
  /** Ürünün kendi bileşeninin soluk önizlemesi — illüstrasyon çizilmez. */
  readonly preview?: ReactNode;
  readonly previewLabel?: string;
  /**
   * Önizlemenin yeri. Varsayılan `bottom`: kesik üst çizgiyle ayrılmış alt
   * bant. `top`: başlığın ÜSTÜNDE, dört yanı kesik çizgili tek kayıt kartı
   * (referans W2 "Kurumlarım · boş": önizleme → başlık → metin → eylem).
   */
  readonly previewPlacement?: 'top' | 'bottom';
  /**
   * Dış çerçeve. Varsayılan `solid`: 1px kenar, 16 köşe, `elevation-sm` gölge.
   * `dashed`: 1.5px kesik kenar, 8 köşe, 28/24 dolgu, gölgesiz (referans
   * organizatör sayfası "hiç etkinlik yok", README 4.7). Kesik çerçevenin
   * içinde önizleme ikinci bir kesik kutu çizmez; ürünün kartı doğrudan durur.
   */
  readonly frame?: 'solid' | 'dashed';
  /**
   * Başlığın üstündeki simge alanı (EmptyState kartı `wb-empty__icon`: 56 px
   * `surface` daire, `primary` simge). Verilmezse çizilmez.
   */
  readonly icon?: ReactNode;
  /**
   * BAĞ ÇİZGİLİ VARYANT (EmptyState kartı): tanışma ile ilgili boş ekranlarda
   * (cüzdan, görüşmeler) simge yerine iki boş avatar ve aralarında bağ çizgisi.
   * Bağ çizgisinin boş ekrandaki tek yeridir; `icon` ile birlikte verilmez.
   */
  readonly bond?: boolean;
}

/**
 * Boş durum (EmptyState kartı, yeni görünüm Aşama 1b): başlık durumu söyler,
 * açıklama listenin ne zaman dolacağını, eylem oraya götüren adımı. Ürünün
 * bugünkü ek parçaları (en fazla üç numaralı ilk adım, soluk örnek kayıt) İ2
 * gereği korunur. Sıfır sayı sergilenmez; jenerik "kayıt yok" yasaktır.
 */
export function EmptyState({
  kicker,
  title,
  titleAs: TitleTag = 'p',
  body,
  steps,
  action,
  preview,
  previewLabel,
  previewPlacement = 'bottom',
  frame = 'solid',
  icon,
  bond = false,
}: EmptyStateProps) {
  const shownSteps = steps?.slice(0, 3) ?? [];
  const previewBlock = preview ? (
    <div
      className={previewPlacement === 'top' ? styles.previewTop : styles.preview}
      aria-hidden="true"
      data-preview-placement={previewPlacement}
    >
      {previewLabel ? <p className={styles.previewLabel}>{previewLabel}</p> : null}
      {preview}
    </div>
  ) : null;

  return (
    <div
      className={frame === 'dashed' ? `${styles.empty} ${styles.dashed}` : styles.empty}
      data-frame={frame}
    >
      {previewPlacement === 'top' ? previewBlock : null}
      {bond ? (
        <span className={styles.bond} aria-hidden="true" data-empty-bond="">
          <span className={styles.emptyAvatar}>
            <User size={24} strokeWidth={1.8} />
          </span>
          <ConnectionLine width={72} height={22} direction="up" />
          <span className={styles.emptyAvatar}>
            <User size={24} strokeWidth={1.8} />
          </span>
        </span>
      ) : icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {kicker ? <p className={styles.kicker}>{kicker}</p> : null}
      <TitleTag className={styles.title}>{title}</TitleTag>
      {body ? <p className={styles.body}>{body}</p> : null}

      {shownSteps.length > 0 ? (
        <ol className={styles.steps}>
          {shownSteps.map((step, index) => {
            const content = (
              <>
                <span className={styles.stepNumber} aria-hidden="true">
                  {index + 1}
                </span>
                <span className={styles.stepText}>
                  {step.title}
                  {step.body ? <span className={styles.stepBody}>{step.body}</span> : null}
                </span>
              </>
            );
            return (
              <li key={step.title}>
                {step.href ? (
                  <a className={styles.step} href={step.href}>
                    {content}
                  </a>
                ) : (
                  <div className={styles.step}>{content}</div>
                )}
              </li>
            );
          })}
        </ol>
      ) : null}

      {action ? <div className={styles.action}>{action}</div> : null}

      {previewPlacement === 'bottom' ? previewBlock : null}
    </div>
  );
}
